// ============================================================================
// APP METADATA
// ============================================================================

import type { ChartImage, DropdownOption, LivePlotImageOptions, LivePlotSizeOptions, PlotSpec, PrintChartAspect, RichSummary } from '../shared';

export const APP_NAME = 'iConcreteElement';
export const APP_VERSION = '1.1.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };
export { SUPPORT_TYPES, ORIENTATION_TYPES, DIF_TYPES } from '../shared';

// ============================================================================
// TYPES
// ============================================================================

export type APIPayload = {
  title: string;
  L: string;
  W: string;
  wadd: string;
  supports: number;
  orientation: number;
  sectionType: number;
  b: string;
  h: string;
  dia: string;
  tf: string;
  tw: string;
  rebar: string;
  rebarDepth: string;
  cover: string;
  shearRebar: string;
  fy: string;
  fc: string;
  dif: number;
  axialLoad: string;
  Pa: string; // pressure history csv
  td: string; // time history csv
  d0: string; // initial displacement
  v0: string; // initial velocity
};

export type ChartKey = 'chart_load' | 'chart_disp' | 'chart_rot' | 'chart_reaction' | 'chart_resistance' | 'chart_PM';

export const LIVE_CHART_ORDER: ChartKey[] = ['chart_disp', 'chart_rot', 'chart_load', 'chart_reaction', 'chart_resistance', 'chart_PM'];

export const PRINT_CHART_ORDER: ChartKey[] = ['chart_load', 'chart_disp', 'chart_rot', 'chart_reaction', 'chart_resistance', 'chart_PM'];

export const PRINT_CHART_ASPECT = {
  chart_load: 'wide',
  chart_disp: 'wide',
  chart_rot: 'wide',
  chart_reaction: 'wide',
  chart_resistance: 'square',
  chart_PM: 'square'
} satisfies Record<ChartKey, PrintChartAspect>;

export const PRINT_CHART_IMAGE_OPTIONS: Record<PrintChartAspect, LivePlotImageOptions> = {
  wide: {
    width: 600,
    height: 400,
    scale: 2,
    layout: {
      font: { size: 16 },
      title: { font: { size: 28 } },
      margin: { l: 86, r: 28, t: 72, b: 108 },
      xaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      yaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      legend: { font: { size: 18 }, x: 0.5, xanchor: 'center', y: -0.32 }
    }
  },
  square: {
    width: 600,
    height: 600,
    scale: 2,
    layout: {
      font: { size: 15 },
      title: { font: { size: 28 } },
      margin: { l: 88, r: 28, t: 78, b: 114 },
      xaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      yaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      legend: { font: { size: 18 }, x: 0.5, xanchor: 'center', y: -0.32 }
    }
  }
};

export const LIVE_PLOT_CARD_MIN_HEIGHT = 'clamp(18rem, 70vw, 25rem)';

export const LIVE_PLOT_SIZE_BY_KEY = {
  chart_load: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '4 / 3'
  },
  chart_disp: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '4 / 3'
  },
  chart_rot: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '4 / 3'
  },
  chart_reaction: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '4 / 3'
  },
  chart_resistance: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '1 / 1'
  },
  chart_PM: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '1 / 1'
  }
} satisfies Record<ChartKey, LivePlotSizeOptions>;

export interface APIResponse {
  summary: RichSummary;
  plotData: Partial<Record<ChartKey, PlotSpec>>;
  cross_section?: ChartImage;
}

// Frontend state for this page after analysis. The API returns summary + plotData only;
// `charts` is generated later from mounted LivePlot components for print/save.
export type ConcreteElementResultState = APIResponse & {
  charts?: Partial<Record<ChartKey, ChartImage>>;
};

// Dropdown option for section types
export const SECTION_TYPES: DropdownOption[] = [
  { value: 0, label: 'Rectangular' },
  { value: 1, label: 'Circular' },
  { value: 2, label: 'Tee' }
];

// ============================================================================
// URL PARAMETER MAPPING
// ============================================================================

/**
 * Mapping from URL parameter names to form field names
 * Used for parsing ?inp={...} query params
 */
export const URL_FIELD_MAP: Record<string, string> = {
  title: 'title',
  L: 'length',
  supports: 'supports',
  W: 'tributaryWidth',
  wadd: 'addedWeight',
  orientation: 'orientation',
  sectionType: 'sectionType',
  b: 'b',
  h: 'h',
  tf: 'tf',
  tw: 'tw',
  rebar: 'rebar',
  rebarDepth: 'rebarDepth',
  shearRebar: 'shearRebar',
  fy: 'fy',
  fc: 'fc',
  dif: 'dif',
  P: 'axialLoad',
  Pa: 'pressureHistory',
  td: 'timeHistory',
  d0: 'u0',
  v0: 'v0'
};

/**
 * Value mappings for URL params (e.g., '0' -> 'pin-pin')
 */
export { URL_VALUE_MAPS } from '../shared';
